import * as React from 'react';
import { ChevronRight, Layers } from 'lucide-react';
import type { Territory } from '@hetivo/hetivo-lib-types';
import { cn } from '@hetivo/hetivo-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TerritoryRowProps {
  canSelectTerritory: boolean;
  onBrowseTerritory: (territoryId: string, territoryName: string) => void;
  onClose: () => void;
  siteCount: number;
  territory: Pick<Territory, 'id' | 'name'>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TerritoryRow({
  canSelectTerritory,
  onBrowseTerritory,
  onClose,
  siteCount,
  territory,
}: TerritoryRowProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { selectTerritory, territoryId: currentTerritoryId } = useMasterContext();
  const isCurrentTerritory = territory.id === currentTerritoryId;
  const locationLabel = siteCount === 1 ? t('mainSelector.location') : t('mainSelector.locationsCount');

  const handleRowClick = (): void => {
    if (canSelectTerritory) {
      selectTerritory(territory.id);
    }
    onBrowseTerritory(territory.id, territory.name);
  };

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    selectTerritory(territory.id);
    onClose();
  };

  const handleChevronClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onBrowseTerritory(territory.id, territory.name);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors cursor-pointer group/territory-row',
        isCurrentTerritory ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={handleRowClick}
    >
      <Layers className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="flex-1 text-xs truncate">{territory.name}</span>

      {/* Site count / Select toggle */}
      <button
        className="text-[11px] shrink-0"
        onClick={canSelectTerritory ? handleSelectClick : undefined}
        type="button"
      >
        <span className={cn('text-muted-foreground', canSelectTerritory && 'group-hover/territory-row:hidden')}>
          {siteCount} {locationLabel}
        </span>
        {canSelectTerritory && (
          <span className="text-primary font-medium hidden group-hover/territory-row:inline">
            {t('mainSelector.select')}
          </span>
        )}
      </button>

      {/* Browse chevron — always visible */}
      <button
        aria-label={`Browse sites in ${territory.name}`}
        className="p-0.5 rounded hover:bg-accent transition-colors shrink-0"
        onClick={handleChevronClick}
        type="button"
      >
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
